import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import MerchantHeader from '../components/MerchantHeader';
import { toast } from 'sonner';
import { 
  Users, Bell, Mail, Plus, Trash2, Edit2, Send,
  Check, X, Shield, Eye, Settings, AlertTriangle
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function TeamAlertsPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [team, setTeam] = useState([]);
  const [alertPrefs, setAlertPrefs] = useState(null);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteName, setInviteName] = useState('');
  const [inviteRole, setInviteRole] = useState('viewer');
  const [inviting, setInviting] = useState(false);
  const [saving, setSaving] = useState(false);

  const getToken = () => sessionStorage.getItem('session_token');

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const loadUserProfile = useCallback(async () => {
    const token = getToken();
    if (!token) {
      navigate('/login');
      return null;
    }

    try {
      const response = await fetch(`${API_URL}/api/auth/profile`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) throw new Error('Failed to load profile');
      
      const data = await response.json();
      setUser(data.member);
      return data.member;
    } catch (error) {
      console.error('Profile error:', error);
      navigate('/login');
      return null;
    }
  }, [navigate]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const loadTeam = useCallback(async () => {
    const token = getToken();
    try {
      const response = await fetch(`${API_URL}/api/integration/team`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setTeam(data.team || []);
      }
    } catch (error) {
      console.error('Team error:', error);
    }
  }, []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const loadAlertPrefs = useCallback(async () => {
    const token = getToken();
    try {
      const response = await fetch(`${API_URL}/api/integration/alerts`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setAlertPrefs(data);
      } else {
        // Set default alert preferences if not found
        setAlertPrefs({
          integration_health_email: true,
          integration_health_inapp: true,
          ci_score_email: false,
          ci_score_inapp: true,
          weekly_summary_email: true,
          weekly_summary_inapp: false,
          alert_recipients: []
        });
      }
    } catch (error) {
      console.error('Alert prefs error:', error);
      // Set default on error
      setAlertPrefs({
        integration_health_email: true,
        integration_health_inapp: true,
        ci_score_email: false,
        ci_score_inapp: true,
        weekly_summary_email: true,
        weekly_summary_inapp: false,
        alert_recipients: []
      });
    }
  }, []);

  useEffect(() => {
    const init = async () => {
      await loadUserProfile();
      await Promise.all([loadTeam(), loadAlertPrefs()]);
      setLoading(false);
    };
    init();
  }, [loadUserProfile, loadTeam, loadAlertPrefs]);

  const handleInvite = async () => {
    if (!inviteEmail.trim()) {
      toast.error('Please enter an email address');
      return;
    }

    setInviting(true);
    const token = getToken();

    try {
      const response = await fetch(`${API_URL}/api/integration/team/invite`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          email: inviteEmail,
          name: inviteName || null,
          role: inviteRole
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to send invite');
      }

      toast.success('Invitation sent!', {
        description: `Invited ${inviteEmail} as ${inviteRole}`
      });
      setShowInviteModal(false);
      setInviteEmail('');
      setInviteName('');
      setInviteRole('viewer');
      await loadTeam();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setInviting(false);
    }
  };

  const handleResendInvite = async (memberId, email) => {
    const token = getToken();
    try {
      const response = await fetch(`${API_URL}/api/integration/team/${memberId}/resend-invite`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to resend invite');
      }

      toast.success('Invitation resent!', {
        description: `Sent to ${email}`
      });
      await loadTeam();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleRemoveMember = async (memberId) => {
    if (!window.confirm('Are you sure you want to remove this team member?')) {
      return;
    }

    const token = getToken();
    try {
      const response = await fetch(`${API_URL}/api/integration/team/${memberId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to remove member');
      }

      toast.success('Team member removed');
      await loadTeam();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleSaveAlertPrefs = async () => {
    setSaving(true);
    const token = getToken();

    try {
      const response = await fetch(`${API_URL}/api/integration/alerts`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(alertPrefs)
      });

      if (!response.ok) {
        throw new Error('Failed to save preferences');
      }

      toast.success('Alert preferences saved');
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem('session_token');
    navigate('/login');
  };

  const roleIcons = {
    owner: Shield,
    admin: Settings,
    viewer: Eye
  };

  const roleColors = {
    owner: 'bg-purple-500/10 text-purple-400 border-purple-500/30',
    admin: 'bg-blue-500/10 text-blue-400 border-blue-500/30',
    viewer: 'bg-gray-500/10 text-gray-400 border-gray-500/30'
  };

  const statusColors = {
    active: 'bg-green-500/10 text-green-400 border-green-500/30',
    invited: 'bg-yellow-500/10 text-yellow-400 border-yellow-500/30',
    disabled: 'bg-red-500/10 text-red-400 border-red-500/30'
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0B1120]">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-400">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0B1120]">
      <MerchantHeader user={user} onLogout={handleLogout} />
      
      <main className="max-w-5xl mx-auto px-6 py-8">
        {/* Page Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white mb-2">Team & Alerts</h1>
          <p className="text-gray-400">
            Manage who has access to your Commerce Index dashboard and who receives notifications.
          </p>
        </div>

        <Tabs defaultValue="team" className="space-y-6">
          <TabsList className="bg-[#0D1526] border border-[#1E3A5F]">
            <TabsTrigger value="team" className="data-[state=active]:bg-blue-600">
              <Users className="w-4 h-4 mr-2" />
              Team Members
            </TabsTrigger>
            <TabsTrigger value="alerts" className="data-[state=active]:bg-blue-600">
              <Bell className="w-4 h-4 mr-2" />
              Alerts & Notifications
            </TabsTrigger>
          </TabsList>

          {/* Team Members Tab */}
          <TabsContent value="team">
            <Card className="bg-[#0D1526] border-[#1E3A5F]">
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="text-white flex items-center gap-2">
                    <Users className="w-5 h-5 text-blue-400" />
                    Team Members
                  </CardTitle>
                  <CardDescription className="text-gray-400 mt-1">
                    Invite teammates to help manage your Commerce Index integration, view signals, and respond to alerts.
                  </CardDescription>
                </div>
                <Button
                  onClick={() => setShowInviteModal(true)}
                  className="bg-blue-600 hover:bg-blue-700"
                  data-testid="invite-team-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Invite Team Member
                </Button>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-[#1E3A5F]">
                        <th className="text-left py-3 px-4 text-gray-400 font-medium text-sm">Name</th>
                        <th className="text-left py-3 px-4 text-gray-400 font-medium text-sm">Email</th>
                        <th className="text-left py-3 px-4 text-gray-400 font-medium text-sm">Role</th>
                        <th className="text-left py-3 px-4 text-gray-400 font-medium text-sm">Status</th>
                        <th className="text-left py-3 px-4 text-gray-400 font-medium text-sm">Last Active</th>
                        <th className="text-right py-3 px-4 text-gray-400 font-medium text-sm">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {team.map((member) => {
                        const RoleIcon = roleIcons[member.role] || Eye;
                        return (
                          <tr key={member.id} className="border-b border-[#1E3A5F]/50 hover:bg-[#1E293B]/30">
                            <td className="py-3 px-4">
                              <span className="text-white font-medium">{member.name || '—'}</span>
                            </td>
                            <td className="py-3 px-4 text-gray-300">{member.email}</td>
                            <td className="py-3 px-4">
                              <Badge variant="outline" className={roleColors[member.role]}>
                                <RoleIcon className="w-3 h-3 mr-1" />
                                {member.role}
                              </Badge>
                            </td>
                            <td className="py-3 px-4">
                              <Badge variant="outline" className={statusColors[member.status]}>
                                {member.status}
                              </Badge>
                            </td>
                            <td className="py-3 px-4 text-gray-400 text-sm">
                              {member.last_active_at 
                                ? new Date(member.last_active_at).toLocaleDateString()
                                : member.status === 'invited' ? 'Pending' : '—'
                              }
                            </td>
                            <td className="py-3 px-4 text-right">
                              {member.role !== 'owner' && (
                                <div className="flex items-center justify-end gap-2">
                                  {member.status === 'invited' && (
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => handleResendInvite(member.id, member.email)}
                                      className="text-gray-400 hover:text-white"
                                      title="Resend invite"
                                      data-testid={`resend-invite-${member.id}`}
                                    >
                                      <Send className="w-4 h-4" />
                                    </Button>
                                  )}
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleRemoveMember(member.id)}
                                    className="text-gray-400 hover:text-red-400"
                                    title="Remove member"
                                    data-testid={`remove-member-${member.id}`}
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </div>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* Roles Explanation */}
                <div className="mt-6 p-4 bg-[#1E293B]/50 rounded-lg border border-[#334155]">
                  <h4 className="text-white font-medium mb-3">Role Permissions</h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                    <div>
                      <div className="flex items-center gap-2 text-purple-400 mb-1">
                        <Shield className="w-4 h-4" />
                        <span className="font-medium">Owner</span>
                      </div>
                      <p className="text-gray-400">Full access including billing, settings, and API keys. Can invite/remove members.</p>
                    </div>
                    <div>
                      <div className="flex items-center gap-2 text-blue-400 mb-1">
                        <Settings className="w-4 h-4" />
                        <span className="font-medium">Admin</span>
                      </div>
                      <p className="text-gray-400">Full access except billing. Can manage integrations and view all data.</p>
                    </div>
                    <div>
                      <div className="flex items-center gap-2 text-gray-400 mb-1">
                        <Eye className="w-4 h-4" />
                        <span className="font-medium">Viewer</span>
                      </div>
                      <p className="text-gray-400">Read-only access to dashboards, signals, and logs.</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Alerts Tab */}
          <TabsContent value="alerts">
            <Card className="bg-[#0D1526] border-[#1E3A5F]">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Bell className="w-5 h-5 text-yellow-400" />
                  Alerts & Notifications
                </CardTitle>
                <CardDescription className="text-gray-400">
                  Choose who should be notified when your Commerce Index integration changes state.
                </CardDescription>
              </CardHeader>
              <CardContent>
                {alertPrefs && (
                  <div className="space-y-6">
                    {/* Alert Matrix */}
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b border-[#1E3A5F]">
                            <th className="text-left py-3 px-4 text-gray-400 font-medium text-sm">Alert Type</th>
                            <th className="text-center py-3 px-4 text-gray-400 font-medium text-sm">
                              <Mail className="w-4 h-4 inline mr-1" />
                              Email
                            </th>
                            <th className="text-center py-3 px-4 text-gray-400 font-medium text-sm">
                              <Bell className="w-4 h-4 inline mr-1" />
                              In-App
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr className="border-b border-[#1E3A5F]/50">
                            <td className="py-4 px-4">
                              <div>
                                <p className="text-white font-medium">Integration Health</p>
                                <p className="text-gray-500 text-sm">When events stop flowing or CI Score becomes stale</p>
                              </div>
                            </td>
                            <td className="py-4 px-4 text-center">
                              <Switch
                                checked={alertPrefs.integration_health_email}
                                onCheckedChange={(checked) => 
                                  setAlertPrefs({...alertPrefs, integration_health_email: checked})
                                }
                                data-testid="alert-health-email"
                              />
                            </td>
                            <td className="py-4 px-4 text-center">
                              <Switch
                                checked={alertPrefs.integration_health_inapp}
                                onCheckedChange={(checked) => 
                                  setAlertPrefs({...alertPrefs, integration_health_inapp: checked})
                                }
                                data-testid="alert-health-inapp"
                              />
                            </td>
                          </tr>
                          <tr className="border-b border-[#1E3A5F]/50">
                            <td className="py-4 px-4">
                              <div>
                                <p className="text-white font-medium">CI Score Changes</p>
                                <p className="text-gray-500 text-sm">When brand or product scores cross key thresholds</p>
                              </div>
                            </td>
                            <td className="py-4 px-4 text-center">
                              <Switch
                                checked={alertPrefs.ci_score_email}
                                onCheckedChange={(checked) => 
                                  setAlertPrefs({...alertPrefs, ci_score_email: checked})
                                }
                                data-testid="alert-score-email"
                              />
                            </td>
                            <td className="py-4 px-4 text-center">
                              <Switch
                                checked={alertPrefs.ci_score_inapp}
                                onCheckedChange={(checked) => 
                                  setAlertPrefs({...alertPrefs, ci_score_inapp: checked})
                                }
                                data-testid="alert-score-inapp"
                              />
                            </td>
                          </tr>
                          <tr>
                            <td className="py-4 px-4">
                              <div>
                                <p className="text-white font-medium">Weekly Summary</p>
                                <p className="text-gray-500 text-sm">Brand views, purchases, top movers, and CI Score trends</p>
                              </div>
                            </td>
                            <td className="py-4 px-4 text-center">
                              <Switch
                                checked={alertPrefs.weekly_summary_email}
                                onCheckedChange={(checked) => 
                                  setAlertPrefs({...alertPrefs, weekly_summary_email: checked})
                                }
                                data-testid="alert-weekly-email"
                              />
                            </td>
                            <td className="py-4 px-4 text-center">
                              <Switch
                                checked={alertPrefs.weekly_summary_inapp}
                                onCheckedChange={(checked) => 
                                  setAlertPrefs({...alertPrefs, weekly_summary_inapp: checked})
                                }
                                data-testid="alert-weekly-inapp"
                              />
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>

                    {/* Alert Recipients */}
                    <div className="border-t border-[#1E3A5F] pt-6">
                      <h4 className="text-white font-medium mb-3 flex items-center gap-2">
                        <AlertTriangle className="w-4 h-4 text-yellow-400" />
                        Critical Alert Recipients
                      </h4>
                      <p className="text-gray-400 text-sm mb-4">
                        These email addresses will receive critical alerts like integration failures.
                        You can add team member emails or shared distribution lists.
                      </p>
                      <div className="flex flex-wrap gap-2 mb-4">
                        {alertPrefs.alert_recipients?.map((email, index) => (
                          <Badge 
                            key={index}
                            variant="outline" 
                            className="bg-[#1E293B] text-white border-[#334155] px-3 py-1"
                          >
                            {email}
                            <button
                              onClick={() => {
                                const newRecipients = alertPrefs.alert_recipients.filter((_, i) => i !== index);
                                setAlertPrefs({...alertPrefs, alert_recipients: newRecipients});
                              }}
                              className="ml-2 text-gray-400 hover:text-red-400"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </Badge>
                        ))}
                      </div>
                      <div className="flex gap-2">
                        <Input
                          type="email"
                          placeholder="Add email address..."
                          className="bg-[#1E293B] border-[#334155] text-white max-w-xs"
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && e.target.value) {
                              const newRecipients = [...(alertPrefs.alert_recipients || []), e.target.value];
                              setAlertPrefs({...alertPrefs, alert_recipients: newRecipients});
                              e.target.value = '';
                            }
                          }}
                        />
                        <Button
                          variant="outline"
                          className="border-[#334155] text-gray-300"
                          onClick={(e) => {
                            const input = e.target.previousSibling;
                            if (input?.value) {
                              const newRecipients = [...(alertPrefs.alert_recipients || []), input.value];
                              setAlertPrefs({...alertPrefs, alert_recipients: newRecipients});
                              input.value = '';
                            }
                          }}
                        >
                          Add
                        </Button>
                      </div>
                    </div>

                    {/* Save Button */}
                    <div className="flex justify-end pt-4 border-t border-[#1E3A5F]">
                      <Button
                        onClick={handleSaveAlertPrefs}
                        disabled={saving}
                        className="bg-blue-600 hover:bg-blue-700"
                        data-testid="save-alerts-btn"
                      >
                        {saving ? 'Saving...' : 'Save Preferences'}
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>

      {/* Invite Modal */}
      <Dialog open={showInviteModal} onOpenChange={setShowInviteModal}>
        <DialogContent className="bg-[#0D1526] border-[#1E3A5F] text-white">
          <DialogHeader>
            <DialogTitle className="text-white">Invite Team Member</DialogTitle>
            <DialogDescription className="text-gray-400">
              We&apos;ll send an email invitation with a link to join your Commerce Index workspace.
              You can manage or revoke access at any time.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="invite-email" className="text-gray-300">Email Address</Label>
              <Input
                id="invite-email"
                type="email"
                placeholder="colleague@company.com"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                className="bg-[#1E293B] border-[#334155] text-white mt-1"
                data-testid="invite-email-input"
              />
            </div>
            <div>
              <Label htmlFor="invite-name" className="text-gray-300">Name (optional)</Label>
              <Input
                id="invite-name"
                placeholder="John Doe"
                value={inviteName}
                onChange={(e) => setInviteName(e.target.value)}
                className="bg-[#1E293B] border-[#334155] text-white mt-1"
              />
            </div>
            <div>
              <Label htmlFor="invite-role" className="text-gray-300">Role</Label>
              <Select value={inviteRole} onValueChange={setInviteRole}>
                <SelectTrigger className="bg-[#1E293B] border-[#334155] text-white mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#1E293B] border-[#334155]">
                  <SelectItem value="viewer" className="text-white">Viewer (read-only)</SelectItem>
                  <SelectItem value="admin" className="text-white">Admin (full access except billing)</SelectItem>
                  <SelectItem value="owner" className="text-white">Owner (full access)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowInviteModal(false)}
              className="border-[#334155] text-gray-300"
            >
              Cancel
            </Button>
            <Button
              onClick={handleInvite}
              disabled={inviting}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="confirm-invite-btn"
            >
              {inviting ? 'Sending...' : 'Send Invite'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
